<script setup>
import { onMounted, reactive, ref } from "vue";
import apiClient from "@/apiClient.js";
import SupplierForm from "./SupplierForm.vue";

const props = defineProps({
  initialCompany: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["cancel", "created"]);

const categories = ref([]);
const loadingCategories = ref(true);
const saving = ref(false);
const error = ref("");
const form = reactive({
  company: props.initialCompany.trim(),
  category_id: "",
  vat_number: "",
  website: "",
  address: "",
});

const updateForm = (value) => {
  Object.assign(form, value);
};

const loadCategories = async () => {
  loadingCategories.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.get("/api/supplier/categories", {
      suppressErrorAlert: true,
    });
    categories.value = Array.isArray(data) ? data : [];
  } catch (loadError) {
    error.value =
      loadError?.response?.data?.message ??
      "Unable to load supplier categories.";
  } finally {
    loadingCategories.value = false;
  }
};

const saveSupplier = async () => {
  if (saving.value) {
    return;
  }

  if (!form.company.trim() || !form.category_id || !form.address.trim()) {
    error.value = "Company, category and address are required.";
    return;
  }

  saving.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.post(
      "/api/supplier",
      {
        ...form,
        company: form.company.trim(),
      },
      {
        suppressErrorAlert: true,
      },
    );
    emit("created", data);
  } catch (saveError) {
    error.value =
      saveError?.response?.data?.message ?? "Unable to create supplier.";
  } finally {
    saving.value = false;
  }
};

onMounted(loadCategories);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 px-4 py-8"
    role="dialog"
    aria-modal="true"
    aria-labelledby="create-supplier-title"
  >
    <form
      class="max-h-full w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800"
      @submit.prevent="saveSupplier"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <h2 id="create-supplier-title" class="text-lg font-semibold">
            Add Supplier
          </h2>
          <p class="mt-1 text-sm text-slate-500 dark:text-slate-300">
            Create this supplier and return it to the transaction assignment.
          </p>
        </div>
        <button
          type="button"
          class="btn"
          :disabled="saving"
          @click="emit('cancel')"
        >
          Close
        </button>
      </div>

      <div v-if="loadingCategories" class="mt-6 text-sm text-slate-500">
        Loading supplier categories…
      </div>
      <SupplierForm
        v-else
        class="mt-6"
        :model-value="form"
        :categories="categories"
        @update:model-value="updateForm"
      />

      <p v-if="error" class="mt-4 text-sm text-rose-600" role="alert">
        {{ error }}
      </p>

      <div class="mt-6 flex justify-end gap-2">
        <button
          type="button"
          class="btn"
          :disabled="saving"
          @click="emit('cancel')"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="btn accent"
          :disabled="saving || loadingCategories"
        >
          {{ saving ? "Creating…" : "Create & Select" }}
        </button>
      </div>
    </form>
  </div>
</template>
